import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import { MarketDataProvider, useMarketData } from './contexts/MarketDataContext';
import { BybitDataProvider, useBybitData } from './contexts/BybitDataContext';
import CryptoHoldings from './components/CryptoHoldings';
import SpotTradingForms from './components/SpotTradingForms';
import CFDTradingForms from './components/CFDTradingForms';
import FuturesTradingForms from './components/FuturesTradingForms';
import FuturesMyOrders from './components/FuturesMyOrders';
import TradingChart from './components/TradingChart';
import Markets from './components/Markets';
import SpotMyOrders from './components/SpotMyOrders';
import ArbitrageRobotPage from './components/ArbitrageRobotPage';
import EventBettingPage from './components/EventBettingPage';
import CryptoWithdrawalModal from './components/CryptoWithdrawalModal';
import PropFirmChallengePage from './components/PropFirmChallengePage';
import WalletPage from './components/WalletPage';
import ProfilePage from './components/ProfilePage';
import OrderBook from './components/OrderBook';
import PairDetailsPanel from './components/PairDetailsPanel';
import StakingPage from './pages/StakingPage';
import HomePage from './pages/HomePage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import TradingFeesPage from './pages/TradingFeesPage';
import FinnhubWebSocketTest from './components/FinnhubWebSocketTest';
import LoadingScreen from './components/LoadingScreen';
import MarketLoadingScreen from './components/MarketLoadingScreen';
import AuthModal from './components/AuthModal';
import { supabase } from './lib/supabaseClient';
import { useAuth } from './hooks/useAuth';
import { useDatabase } from './hooks/useDatabase';
import { TOP_CRYPTO_PAIRS, CFD_INSTRUMENTS } from './constants/tradingPairs';
import { getUserCfdTier } from './constants/tradingTiers';
import { useUserAssets } from './hooks/useUserAssets';
import { useFuturesTrading } from './hooks/useFuturesTrading';
import { usePropFirmTrading } from './hooks/usePropFirmTrading';
import { useWalletBreakdown } from './hooks/useWalletBreakdown';
import { useUserLeverage } from './hooks/useUserLeverage';
import SwapCryptoPage from './components/SwapCryptoPage';
import SpinTheWheel from './components/SpinTheWheel';
import PaymentSandbox from './components/PaymentSandbox';
import { DisplayCurrencyProvider } from './contexts/DisplayCurrencyContext';
import { tradingPaths, tradingModeForPath } from './routes';
import { isImpersonationCallbackLocation } from './utils/impersonationCallback';

export type TradingMode = 'home' | 'swap' | 'futures' | 'cfd' | 'prop_firm' | 'robot' | 'events' | 'wallet' | 'profile' | 'staking' | 'wheel' | 'payment_sandbox';

export interface FuturesPosition {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  amount: number;
  entryPrice: number;
  currentPrice: number;
  leverage: number;
  marginType: 'isolated' | 'cross';
  liquidationPrice: number;
  unrealizedPnl: number;
  margin: number;
  roi: number;
  created_at: string;
  user_email?: string;
}

export interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'trade' | 'robot_profit' | 'binary_trade' | 'stake' | 'staking_profit' | 'staking_return' | 'challenge_fee' | 'challenge_reward';
  amount: number;
  description: string;
  status: 'completed' | 'pending' | 'failed';
  timestamp: string;
  created_at?: string;
}

// User status tiers based on portfolio value in USD
export type UserStatus = 'No-Coiner' | 'Shrimp' | 'Crab' | 'Octopus' | 'Dolphin' | 'Shark' | 'Whale' | 'Humpback';

function AppContent() {
  const { user, loading: authLoading, signOut } = useAuth();
  const { marketData, snapshotData, getSnapshotPriceBySymbol } = useMarketData();
  const { getPriceBySymbol: getBybitPrice } = useBybitData();
  const {
    balances,
    updateBalances,
    fetchBalances,
    robotState,
    updateRobotState,
    loading: dbLoading,
    transactions,
    addTransaction,
    fetchTransactions,
    userStakes,
    addUserStake,
    eventOutcomes,
    eventBets,
    events,
    addEventBet,
    newsItems,
    portfolioSnapshots,
    createPortfolioSnapshot,
    fetchPortfolioSnapshots,
    kycStatus,
    updateKycStatus,
    referralCode,
    referralCount,
    referredUsers,
    fetchUserStakes,
    fetchRobotState,
    calculateCurrentEarnings,
    cancelUserStake,
    isDemoAccount
  } = useDatabase();
  const { assets, fetchAssets, updateAssetBalance } = useUserAssets();
  const { 
    activePositions, 
    openOrders, 
    fetchActivePositions, 
    fetchOpenOrders, 
    openPosition, 
    closePosition, 
    cancelOrder, 
    cancelAllOpenOrders,
    calculateLiquidationPrice
  } = useFuturesTrading();
  
  const {
    propPositions,
    propOrders,
    propPositionHistory,
    propChallengeAccount,
    fetchPropPositions,
    fetchPropOrders,
    fetchPropPositionHistory,
    fetchPropChallengeAccount,
    initializeChallenge,
    cancelChallenge,
    placePropOrder,
    closePropPosition,
    cancelPropOrder,
    cancelAllPropOrders,
    closeAllPropPositions,
    loading: propFirmLoading
  } = usePropFirmTrading();
  
  // Wallet breakdown for margin tracking
  const {
    totalBalance,
    usedMargin,
    unrealizedPnl,
    availableBalance,
    reserved,
    refreshBreakdown,
    futuresUsedMargin,
    futuresOrdersReserved
  } = useWalletBreakdown(undefined, undefined, undefined, getBybitPrice);

  // Calculate actual available balance for trading (portfolio-wide)
  const actualAvailableBalance = useMemo(() => {
    return Math.max(0, availableBalance);
  }, [availableBalance]);

  const usdtAvailableMargin = useMemo(() => {
    return Math.max(0, balances.usdt_balance - (futuresUsedMargin || 0) - (futuresOrdersReserved || 0));
  }, [balances.usdt_balance, futuresUsedMargin, futuresOrdersReserved]);
  
  const location = useLocation();
  const navigate = useNavigate();
  const isImpersonationCallback = isImpersonationCallbackLocation(location.pathname, location.search);
  const tradingMode = tradingModeForPath(location.pathname);
  const setTradingMode = (mode: TradingMode) => navigate(tradingPaths[mode]);
  const [selectedPair, setSelectedPair] = useState(() => tradingMode === 'cfd' ? 'EUR/USD' : 'BTCUSDT');
  useEffect(() => {
    if (tradingMode === 'cfd') setSelectedPair('EUR/USD');
    if (tradingMode === 'futures') setSelectedPair('BTCUSDT');
  }, [tradingMode]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreparingMarkets, setIsPreparingMarkets] = useState(false);
  
  // Define tickers based on trading mode
  const tickers = tradingMode === 'cfd' ? CFD_INSTRUMENTS : TOP_CRYPTO_PAIRS;

  // Interval reference for position refresh
  const positionRefreshIntervalRef = useRef<number | null>(null);
  
  // Check if this is a password recovery link
  const isRecoveryLink = useMemo(() => {
    const hash = window.location.hash;
    if (!hash) return false;
    
    const hashParams = new URLSearchParams(hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');
    
    return type === 'recovery' && !!accessToken;
  }, []);

  // Add this function inside App component, before the return
const handleUpdatePassword = async (newPassword: string) => {
  try {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to update password:", err);
    return false;
  }
};


  // Detect when user logs in and show market loading screen
  useEffect(() => {
    if (user && !authLoading && !isRecoveryLink) {
      const hasShownLoading = sessionStorage.getItem('marketLoadingShown');

      if (!hasShownLoading) {
        setIsPreparingMarkets(true);
        sessionStorage.setItem('marketLoadingShown', 'true');
      }
    }
  }, [user, authLoading, isRecoveryLink]);


  // Collect all active symbols for WebSocket subscription
  const activeSymbols = useMemo(() => {
    // Always ensure we have at least default symbols to prevent empty array
    const neededSymbols = new Set<string>(['BTCUSDT', 'ETHUSDT']); // Default symbols
    
    // Always include the selected pair (most important)
    if (selectedPair) {
      neededSymbols.add(selectedPair);
    }
    
    // Include symbols with active positions
    activePositions.forEach(p => {
      if (p.symbol) neededSymbols.add(p.symbol);
    });
    
    // Include symbols with prop positions
    propPositions.forEach(p => {
      if (p.symbol) neededSymbols.add(p.symbol);
    });
    
    // Convert to array with selectedPair first
    const symbolsArray = Array.from(neededSymbols);
    const finalSymbols = selectedPair ? 
      [selectedPair, ...symbolsArray.filter(s => s !== selectedPair)] : 
      symbolsArray;
    
    // Limit to 10 symbols max for better coverage
    const limitedSymbols = finalSymbols.slice(0, 10);
    
    return limitedSymbols;
  }, [selectedPair, activePositions, propPositions]);
  
  // Always enable polling as fallback regardless of WebSocket status

  
  // Get current price from ticker or market data
  const currentSelectedPairPrice = useMemo(() => {
    const ticker = (marketData || []).find(t => t.symbol === selectedPair);
    const marketPrice = (marketData || []).find(data => data.symbol === selectedPair)?.price;
    if (marketPrice && marketPrice > 0) {
      return marketPrice;
    }
    
    return 0;
  }, [selectedPair, marketData]);

  // Define ticker variable for compatibility
  const ticker = (marketData || []).find(data => data.symbol === selectedPair);

  // Get live price for a symbol with fallback (using same strategy as WalletPage)
  const getCurrentPrice = useCallback((symbol: string): number => {
    // Strategy 1: Try Bybit WebSocket data first (most real-time)
    const bybitPrice = getBybitPrice(symbol);
    if (bybitPrice > 0) {
      return bybitPrice;
    }

    // Strategy 2: Try snapshot data (stable fallback)
    const snapshotPrice = getSnapshotPriceBySymbol(symbol);
    if (snapshotPrice > 0) {
      return snapshotPrice;
    }

    // Strategy 3: Fallback to database market data
    const dbData = (marketData || []).find(data => data.symbol === symbol);
    return dbData?.price || 0;
  }, [getBybitPrice, getSnapshotPriceBySymbol, marketData]);

  // Get current BTC price from combined data using the same strategy
  const currentBtcPrice = getCurrentPrice('BTCUSDT');

  // Get current price for selected pair
  // Calculate total portfolio value
 const totalPortfolioValue = useMemo(() => {
  let total = balances.usdt_balance; // Start with USDT balance

  // Use the same BTC price as displayed in wallet (real-time price)
  const btcUsdtPrice = getCurrentPrice('BTCUSDT');

  total += balances.btc_balance * btcUsdtPrice; // Add BTC value

  // Add value of other user assets
  const allAssets = assets || [];
  if (allAssets.length > 0) {
    allAssets.forEach(asset => {
    // Skip USDT and BTC as they are handled above
    if (asset.asset_symbol !== 'USDT' && asset.asset_symbol !== 'BTC') {
      // Construct the USDT pair symbol (e.g., 'ETHUSDT')
      const assetUsdtSymbol = `${asset.asset_symbol}USDT`;

      // Use getCurrentPrice for consistency with real-time data
      const assetPrice = getCurrentPrice(assetUsdtSymbol);

      total += asset.balance * assetPrice;
    }
    });
  }

  total += robotState?.allocated_balance || 0;

  return total;
}, [balances, assets, getCurrentPrice, robotState?.allocated_balance]);

  // Function to determine user status based on portfolio value in USD
  const getUserStatus = useCallback((portfolioValue: number): UserStatus => {
    if (portfolioValue < 250) return 'No-Coiner';
    if (portfolioValue < 10000) return 'Shrimp';
    if (portfolioValue < 25000) return 'Crab';
    if (portfolioValue < 50000) return 'Octopus';
    if (portfolioValue < 100000) return 'Dolphin';
    if (portfolioValue < 500000) return 'Shark';
    if (portfolioValue < 2000000) return 'Whale';
    return 'Humpback';
  }, []);

  // Get user status
  const userStatus = getUserStatus(totalPortfolioValue);

  // Calculate user's CFD tier based on portfolio value
  const userCfdTier = useMemo(() => {
    return getUserCfdTier(totalPortfolioValue);
  }, [totalPortfolioValue]);

  const userLeverageSettings = useUserLeverage(user?.id, totalPortfolioValue);

  const currentMaxForexLeverage = useMemo(() => userLeverageSettings.maxForex, [userLeverageSettings]);
  const currentMinForexLeverage = useMemo(() => userLeverageSettings.minForex, [userLeverageSettings]);
  const currentMaxCommoditiesLeverage = useMemo(() => userLeverageSettings.maxCommodities, [userLeverageSettings]);
  const currentMinCommoditiesLeverage = useMemo(() => userLeverageSettings.minCommodities, [userLeverageSettings]);
  const currentMaxStocksLeverage = useMemo(() => userLeverageSettings.maxStocks, [userLeverageSettings]);
  const currentMinStocksLeverage = useMemo(() => userLeverageSettings.minStocks, [userLeverageSettings]);
  const currentMaxFuturesLeverage = useMemo(() => userLeverageSettings.maxFutures, [userLeverageSettings]);
  const currentMinFuturesLeverage = useMemo(() => userLeverageSettings.minFutures, [userLeverageSettings]);

  // Initialize app data - only when user is authenticated
  useEffect(() => {
    // Skip initialization if this is a recovery link
    if (isRecoveryLink) {
      setIsLoading(false);
      return;
    }
    
    const initializeData = async () => {
      // Only fetch user-specific data if user is authenticated
      if (user) {
        await fetchTransactions();
        await fetchAssets();
        await fetchPortfolioSnapshots();
        await fetchUserStakes();
        await fetchActivePositions();
        await fetchOpenOrders();
      } else {
        // For unauthenticated users, no data fetching needed
      }
      setIsLoading(false);
    };
    
    if (!authLoading && !dbLoading) {
      initializeData();
    }
  }, [
    user, 
    authLoading, 
    dbLoading, 
    fetchTransactions, 
    fetchAssets, 
    fetchPortfolioSnapshots, 
    fetchUserStakes,
    fetchActivePositions,
    fetchOpenOrders,
    isRecoveryLink
  ]);

  // Set up periodic refresh of active positions - only for authenticated users
  useEffect(() => {
    // Only set up the interval if the user is logged in
    if (user) {
      // Clear any existing interval first
      if (positionRefreshIntervalRef.current) {
        clearInterval(positionRefreshIntervalRef.current);
      }
      
      // Set up a new interval to refresh positions every 10 seconds (reduced frequency since WebSocket handles real-time updates)
      positionRefreshIntervalRef.current = window.setInterval(async () => {
        
        try {
          // Refresh futures positions
          await fetchActivePositions();
          
          // Refresh prop firm positions if there's an active challenge
          if (propChallengeAccount) {
            await fetchPropPositions(propChallengeAccount.challengeId);
          }
        } catch (error) {
          console.error('Error refreshing positions:', error);
        }
      }, 10000); // 10 seconds interval (reduced since WebSocket provides real-time updates)
      
      // Clean up the interval when the component unmounts
      return () => {
        if (positionRefreshIntervalRef.current) {
          clearInterval(positionRefreshIntervalRef.current);
          positionRefreshIntervalRef.current = null;
        }
      };
    }
  }, [user, fetchActivePositions, fetchPropPositions, propChallengeAccount]);

  // Handle futures trade
  const handleFuturesTrade = useCallback(async (
    symbol: string,
    side: 'long' | 'short', 
    amount: number, 
    leverage: number, 
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType?: 'market' | 'limit',
    price?: number
  ) => {
    try {
      // Use the price parameter directly from the frontend
      const currentPrice = price || 111445.9000; // Use the displayed price as fallback
      await fetchBalances();
      
      // Use the openPosition function from useFuturesTrading hook
      const positionId = await openPosition({
        symbol: symbol || selectedPair,
        side,
        amount,
        leverage,
        marginType,
        orderType: orderType || 'market',
        price: currentPrice,
        stopLoss: stopLoss?.trigger_price,
        takeProfit: takeProfit?.trigger_price,
        contractSize: 1
      });
      
      if (positionId) {
        // Refresh wallet breakdown to update available balance
        refreshBreakdown();

        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error in handleFuturesTrade:', error);
      return false;
    }
  }, [selectedPair, openPosition, refreshBreakdown, fetchBalances]);

  // Handle closing a futures position
  const handleClosePosition = useCallback(async (positionId: string, livePrice?: number) => {
    try {
      // Call the closePosition function from useFuturesTrading hook with live price
      const success = await closePosition(positionId, livePrice);

      if (success) {
        // Refresh wallet breakdown to update available balance
        refreshBreakdown();

        // The closePosition function will handle updating the database and refreshing the positions
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error in handleClosePosition:', error);
      return false;
    }
  }, [closePosition, refreshBreakdown]);

  // Handle spot order
  const handleSpotOrder = useCallback(async (order: {
    side: 'buy' | 'sell';
    order_type: 'market' | 'limit';
    amount: number;
    price?: number;
    stop_loss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number };
    take_profit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number };
  }) => {
    const { side, order_type, amount } = order;
    const price = order.price || currentSelectedPairPrice;
    
    // Calculate total cost/proceeds
    const total = amount * price;
    
    if (side === 'buy') {
      // Check if user has enough USDT
      if (total > balances.usdt_balance) {
        alert('Insufficient USDT balance');
        return;
      }
      
      // Update balances
      await updateBalances({ 
        usdt_balance: balances.usdt_balance - total,
        btc_balance: balances.btc_balance + amount
      });
      
      // Add transaction
      await addTransaction({
        type: 'trade',
        amount: -total,
        description: `Bought ${amount.toFixed(6)} BTC at ${price.toFixed(2)} USDT`,
        status: 'completed'
      });
    } else {
      // Check if user has enough BTC
      if (amount > balances.btc_balance) {
        alert('Insufficient BTC balance');
        return;
      }
      
      // Update balances
      await updateBalances({ 
        usdt_balance: balances.usdt_balance + total,
        btc_balance: balances.btc_balance - amount
      });
      
      // Add transaction
      await addTransaction({
        type: 'trade',
        amount: total,
        description: `Sold ${amount.toFixed(6)} BTC at ${price.toFixed(2)} USDT`,
        status: 'completed'
      });
    }
  }, [currentSelectedPairPrice, balances, updateBalances, addTransaction]);

  // Handle swap between cryptocurrencies
  const handleSwap = useCallback(async (fromSymbol: string, toSymbol: string, fromAmount: number, toAmountReceived?: number) => {
    try {
      // Wait for balances to be fully loaded
      if (dbLoading) {
        throw new Error('Please wait, loading account data...');
      }
      
      // Check available balance for swap
      const swapValueUSD = fromAmount * (fromSymbol === 'USDT' ? 1 : 
                                        fromSymbol === 'BTC' ? currentSelectedPairPrice :
                                        marketData.find(data => data.symbol === `${fromSymbol}USDT`)?.price || 0);
      
      if (swapValueUSD > availableBalance) {
        throw new Error(`Insufficient available balance. Required: ${swapValueUSD.toFixed(2)} USD, Available: ${availableBalance.toFixed(2)} USD`);
      }
      
      // Validate input parameters
      if (!fromSymbol || !toSymbol || isNaN(fromAmount) || fromAmount <= 0) {
        throw new Error('Invalid swap parameters');
      }

      // Additional security validations
      const MIN_SWAP_AMOUNT = 0.0001;
      const MAX_SWAP_AMOUNT_USD = 1000000;
      const MAX_SLIPPAGE = 0.05; // 5% maximum slippage
      const SWAP_FEE_RATE = 0.001; // 0.1% fee

      // Prevent swapping to the same currency
      if (fromSymbol === toSymbol) {
        throw new Error('Cannot swap to the same currency');
      }

      // Check minimum amount
      if (fromAmount < MIN_SWAP_AMOUNT) {
        throw new Error(`Minimum swap amount is ${MIN_SWAP_AMOUNT}`);
      }

      // Helper to get price for any symbol with multiple fallback strategies
      const getPriceForSymbol = (symbol: string): number => {
        // Stablecoins always return 1
        if (symbol === 'USDT' || symbol === 'USDC') {
          return 1;
        }

        const tradingPair = `${symbol}USDT`;

        // Strategy 1: Try websocket data first (most real-time)
        const bybitPrice = getBybitPrice(tradingPair);
        if (bybitPrice > 0) {
          console.log(`App swap: Got price for ${symbol} from websocket: ${bybitPrice}`);
          return bybitPrice;
        }

        // Strategy 2: Try snapshot data (stable fallback)
        const snapshotPrice = getSnapshotPriceBySymbol(tradingPair);
        if (snapshotPrice > 0) {
          console.log(`App swap: Got price for ${symbol} from snapshot: ${snapshotPrice}`);
          return snapshotPrice;
        }

        // Strategy 3: Try direct marketData lookup
        const marketDataItem = marketData.find(item => item.symbol === tradingPair);
        if (marketDataItem && marketDataItem.price > 0) {
          console.log(`App swap: Got price for ${symbol} from marketData: ${marketDataItem.price}`);
          return marketDataItem.price;
        }

        // Strategy 4: Try snapshotData direct lookup
        const snapshotItem = snapshotData.find(item => item.symbol === tradingPair);
        if (snapshotItem && snapshotItem.price > 0) {
          console.log(`App swap: Got price for ${symbol} from snapshotData: ${snapshotItem.price}`);
          return snapshotItem.price;
        }

        console.warn(`App swap: No price found for ${symbol}`);
        return 0;
      };

      // Get current prices
      const fromPrice = getPriceForSymbol(fromSymbol);
      const toPrice = getPriceForSymbol(toSymbol);

      // Validate prices are available and reasonable
      if (!fromPrice || !toPrice || fromPrice <= 0 || toPrice <= 0) {
        throw new Error('Unable to get current prices for swap');
      }

      // Check maximum USD value
      const fromUsdValue = fromAmount * fromPrice;
      if (fromUsdValue > MAX_SWAP_AMOUNT_USD) {
        throw new Error(`Maximum swap amount is $${MAX_SWAP_AMOUNT_USD.toLocaleString()}`);
      }
      
      // If toAmountReceived is not provided, calculate it
      let calculatedToAmount = toAmountReceived;
      if (!calculatedToAmount || isNaN(calculatedToAmount)) {
        const exchangeRate = fromPrice / toPrice;
        calculatedToAmount = fromAmount * exchangeRate * (1 - SWAP_FEE_RATE);
      } else {
        // If toAmountReceived is provided, validate it against expected calculation
        const expectedExchangeRate = fromPrice / toPrice;
        const expectedToAmount = fromAmount * expectedExchangeRate * (1 - SWAP_FEE_RATE);
        
        // Prevent division by zero or extremely small numbers
        if (expectedToAmount <= 0.00000001) {
          throw new Error('Calculated output amount is too small. Please increase the input amount.');
        }
        
        const slippage = Math.abs(calculatedToAmount - expectedToAmount) / expectedToAmount;
        
        // Additional sanity check: if slippage is unreasonably high, it's likely a calculation error
        if (slippage > 1.0) { // More than 100% slippage indicates a serious error
          throw new Error('Price calculation error detected. Please refresh the page and try again.');
        }
        
        if (slippage > MAX_SLIPPAGE) {
          throw new Error(`Price has changed too much (${(slippage * 100).toFixed(2)}% slippage). Please try again.`);
        }
      }
      
      // Validate calculated amount
      if (!calculatedToAmount || isNaN(calculatedToAmount) || calculatedToAmount <= 0) {
        throw new Error('Invalid amount to receive');
      }
      
      // Additional sanity check: ensure the swap ratio is reasonable

      const feeAmount = fromAmount * SWAP_FEE_RATE;

      // Helper to get current balance of any asset
      const getAssetBalance = (symbol: string): number => {
        if (symbol === 'USDT') return balances.usdt_balance;
        if (symbol === 'BTC') return balances.btc_balance;
        const asset = assets.find(a => a.asset_symbol === symbol);
        return asset ? asset.balance : 0;
      };

      const currentFromBalance = getAssetBalance(fromSymbol);
      
      // Final balance check with buffer for fees
      if (fromAmount > currentFromBalance) {
        throw new Error(`Insufficient ${fromSymbol} balance`);
      }

      // Ensure we're not trying to give out more than what's reasonable
      // Instead of checking numeric ratios, check USD values for reasonableness
      const toUsdValue = calculatedToAmount * toPrice;
      const valueRatio = toUsdValue / fromUsdValue;
      
      // The output USD value should be close to input USD value (accounting for fees)
      // Allow up to 10% difference to account for fees and minor price movements
      if (valueRatio > 1.1 || valueRatio < 0.8) {
        console.error('Swap validation failed:', {
          fromAmount,
          calculatedToAmount,
          fromPrice,
          toPrice,
          fromUsdValue,
          toUsdValue,
          valueRatio,
          expectedRatio: 'should be between 0.8 and 1.1'
        });
        throw new Error(`Swap calculation error: USD value mismatch (ratio: ${valueRatio.toFixed(3)}). Please refresh and try again.`);
      }

      // Log the swap for debugging purposes
      console.log('Executing swap:', {
        fromSymbol,
        toSymbol,
        fromAmount,
        calculatedToAmount,
        fromPrice,
        toPrice,
        exchangeRate: fromPrice / toPrice,
        feeAmount,
        fromBalance: currentFromBalance
      });

      // Deduct from fromSymbol balance
      if (fromSymbol === 'USDT') {
        await updateBalances({ usdt_balance: balances.usdt_balance - fromAmount });
      } else if (fromSymbol === 'BTC') {
        await updateBalances({ btc_balance: balances.btc_balance - fromAmount });
      } else {
        await updateAssetBalance(fromSymbol, currentFromBalance - fromAmount);
      }

      // Add to toSymbol balance
      if (toSymbol === 'USDT') {
        await updateBalances({ usdt_balance: balances.usdt_balance + calculatedToAmount });
      } else if (toSymbol === 'BTC') {
        await updateBalances({
          btc_balance: balances.btc_balance + calculatedToAmount
        });
      } else {
        const toAsset = assets.find(a => a.asset_symbol === toSymbol);
        if (toAsset) {
          await updateAssetBalance(toSymbol, toAsset.balance + calculatedToAmount);
        } else {
          // If the target asset doesn't exist for the user, create it
          // This will insert a new row in user_assets table
          // The updateAssetBalance function already handles this logic
          await updateAssetBalance(toSymbol, calculatedToAmount);
        }
      }
      
      // Add transaction record for swap
      await addTransaction({
        type: 'trade',
        amount: -fromAmount * getPriceForSymbol(fromSymbol), // Negative amount for the source currency
        description: `Swapped ${fromAmount} ${fromSymbol} to ${calculatedToAmount.toFixed(6)} ${toSymbol} (including 0.1% fee)`,
        status: 'completed'
      });
      
      // Refresh wallet breakdown
      refreshBreakdown();

      console.log('Swap completed successfully');
      return true;
    } catch (error: any) {
      console.error('Swap error details:', {
        error,
        message: error?.message,
        stack: error?.stack
      });
      throw error;
    }
  }, [currentSelectedPairPrice, balances, updateBalances, addTransaction, assets, updateAssetBalance, marketData, dbLoading, availableBalance, refreshBreakdown]);

  // Handle wallet operations (deposit/withdrawal)
  const handleWalletOperation = useCallback(async (type: 'deposit' | 'withdrawal', currency: 'USDT' | 'BTC', amount: number) => {
    if (type === 'deposit') {
      try {
        if (currency === 'USDT') {
          console.log(`Processing USDT deposit of ${amount} for user ${user?.id} (demo: ${user?.user_metadata?.is_demo ? 'yes' : 'no'})`);
          await updateBalances({ usdt_balance: balances.usdt_balance + amount });
        } else {
          await updateBalances({ btc_balance: balances.btc_balance + amount });
        }

        // Add transaction - this will trigger the demo account reset if user is a demo user
        const description = user?.user_metadata?.is_demo
          ? `Initial deposit to convert demo account to live account: ${amount} ${currency}`
          : `Deposited ${amount} ${currency}`;

        const { data: txData, error: txError } = await addTransaction({
          type: 'deposit',
          amount,
          description,
          status: 'completed'
        });

        if (txError) {
          console.error('Error processing deposit transaction:', txError);
          throw new Error(`Failed to process deposit: ${txError.message}`);
        }

        if (currency === 'USDT' && user?.id && txData) {
          try {
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-giveaway-tickets`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify({
                userId: user.id,
                depositAmount: amount,
                transactionId: txData.id || '',
              }),
            });

            if (response.ok) {
              const result = await response.json();
              if (result.success && result.ticketsEarned > 0) {
                console.log(`Earned ${result.ticketsEarned} giveaway tickets!`);
                alert(`Deposit successful! You earned ${result.ticketsEarned} giveaway ticket${result.ticketsEarned > 1 ? 's' : ''}!`);
              }
            }
          } catch (giveawayError) {
            console.error('Error processing giveaway tickets:', giveawayError);
          }
        }
      } catch (error) {
        console.error('Error processing deposit:', error);
        alert('Failed to process deposit. Please try again.');
      }
    } else {
      // Check available balance for withdrawal
      const withdrawalValueUSD = currency === 'USDT' ? amount : amount * currentSelectedPairPrice;
      
      if (withdrawalValueUSD > availableBalance) {
        alert(`Insufficient available balance. Required: ${withdrawalValueUSD.toFixed(2)} USD, Available: ${availableBalance.toFixed(2)} USD`);
        return;
      }
      
      // Check balance
      if (currency === 'USDT' && amount > balances.usdt_balance) {
        alert('Insufficient USDT balance');
        return;
      } else if (currency === 'BTC' && amount > balances.btc_balance) {
        alert('Insufficient BTC balance');
        return;
      }
      
      if (currency === 'USDT') {
        await updateBalances({ usdt_balance: balances.usdt_balance - amount });
      } else {
        await updateBalances({ btc_balance: balances.btc_balance - amount });
      }
      
      // Add transaction
      await addTransaction({
        type: 'withdrawal',
        amount: -amount,
        description: `Withdrawn ${amount} ${currency}`,
        status: 'completed'
      });
      
      // Refresh data after withdrawal
      await fetchBalances();
      await fetchAssets();
      await fetchTransactions();
    }
    
    // Refresh wallet breakdown
    refreshBreakdown();
  }, [balances, updateBalances, addTransaction, fetchBalances, fetchAssets, fetchTransactions, availableBalance, currentSelectedPairPrice, refreshBreakdown]);

  // Handle staking
  const handleStake = useCallback(async (asset: string, amount: number, duration: number) => {
    try {
      // Check available balance for staking
      const stakingValueUSD = asset === 'USDT' ? amount : amount * currentSelectedPairPrice;
      
      if (stakingValueUSD > availableBalance) {
        throw new Error(`Insufficient available balance. Required: ${stakingValueUSD.toFixed(2)} USD, Available: ${availableBalance.toFixed(2)} USD`);
      }
      
      // Check minimum stake
      if (amount < 0.001) {
        setStakingMessage({
          type: 'error',
          text: `Minimum stake amount is 0.001 ${asset}`
        });
        return;
      }
      
      // Check balance
      if (asset === 'USDT' && amount > balances.usdt_balance) {
        throw new Error(`Insufficient ${asset} balance`);
      } else if (asset === 'BTC' && amount > balances.btc_balance) {
        throw new Error(`Insufficient ${asset} balance`);
      }
      
      // Check if user already has an active stake for this asset
      if (hasActiveStakeForAsset(asset)) {
        throw new Error(`You already have an active stake for ${asset}. Please wait for it to complete before creating a new one.`);
      }
      
      // Calculate APY based on duration
      let apy = 5.0; // Base APY
      if (duration >= 90) apy = 7.5;
      if (duration >= 180) apy = 10.0;
      if (duration >= 365) apy = 12.5;
      
      setIsStaking(true);
      
      // Call the addUserStake function
      const success = await addUserStake(
        asset,
        amount,
        apy,
        duration
      );
      
      if (success) {
        setStakingMessage({
          type: 'success',
          text: `Successfully staked ${amount} ${asset} for ${duration} days`
        });
        
        // Close modal and reset form
        setShowConfirmationModal(false);
        setStakingAmount('');
        setSelectedAsset(null);
        
        // Call the onStake prop to update balances
        await onStake(asset, amount, duration);
        
        // Refresh user stakes
        await fetchUserStakes();
        
        // Refresh wallet breakdown
        refreshBreakdown();
      } else {
        throw new Error('Staking failed');
      }
    } catch (error: any) {
      setStakingMessage({
        type: 'error',
        text: error.message || 'Failed to stake. Please try again.'
      });
    } finally {
      setIsStaking(false);
    }
  }, [balances, updateBalances, addTransaction, addUserStake, fetchUserStakes, availableBalance, currentSelectedPairPrice, refreshBreakdown]);

  // Calculate time remaining for a stake
  // CRM temporarily sends impersonation tokens to the app root. Mount the same
  // callback page for both entry points so session handling remains centralized.
  if (isImpersonationCallback) {
    return <AuthCallbackPage />;
  }

  // Show loading state while checking for existing challenge
  if (propFirmLoading && propChallengeAccount === null) {
    return <LoadingScreen message="Loading prop firm challenge data..." />;
  }

  return (
    <DisplayCurrencyProvider key={user?.id ?? 'guest'} userId={user?.id}>
      <div className="min-h-screen bg-slate-900 text-white">
          <Routes>
            {/* Handle password recovery at root path */}
            <Route element={
              isRecoveryLink ? (
                <ResetPasswordPage />
              ) : authLoading ? (
                <LoadingScreen />
              ) : user ? (
                <>
                  <Header
                    tradingMode={tradingMode}
                    setTradingMode={setTradingMode}
                    selectedPair={selectedPair}
                    setSelectedPair={setSelectedPair}
                    currentPrice={currentSelectedPairPrice}
                    marketData={(marketData || []).find(data => data.symbol === selectedPair)}
                    usdtBalance={balances.usdt_balance}
                    btcBalance={balances.btc_balance}
                    totalPortfolioValue={totalPortfolioValue}
                    user={user}
                    signOut={signOut}
                    marketDataList={marketData}
                    userStatus={userStatus}
                    isDemoAccount={isDemoAccount}
                  />
                  
                  <main><Outlet /></main>
                </>
              ) : (
                <Navigate to="/signin" replace state={{ from: location.pathname + location.search }} />
              )
            } >
            <Route index element={
                      <HomePage
                        currentBtcPrice={currentSelectedPairPrice}
                        usdtBalance={balances.usdt_balance}
                        btcBalance={balances.btc_balance}
                        marketData={marketData}
                        futuresPositions={activePositions}
                        transactions={transactions}
                        setTradingMode={setTradingMode}
                        newsItems={newsItems}
                        totalPortfolioValue={totalPortfolioValue}
                      />
            } />
            <Route path={tradingPaths.swap} element={
                      <SwapCryptoPage
                        usdtBalance={balances.usdt_balance}
                        btcBalance={balances.btc_balance}
                        currentBtcPrice={currentSelectedPairPrice}
                        onSwap={handleSwap}
                        userAssets={assets}
                      />
            } />
            <Route path={tradingPaths.futures} element={
                      <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] bg-slate-900 overflow-y-auto">
                        {/* Left Column - Order Book (full width on mobile, fixed width on desktop) */}
                        <div className="hidden lg:block w-full lg:w-80 border-b lg:border-r lg:border-b-0 border-slate-700 h-auto lg:h-full">
                          <OrderBook 
                            currentPrice={currentSelectedPairPrice}
                            selectedPair={selectedPair}
                            tradingMode="futures"
                          />
                        </div>
                        
                        {/* Middle Column - Chart and Trading Forms */}
                        <div className="flex-1 flex flex-col h-full">
                          {/* Trading Chart */}
                          <div className="h-[40vh] lg:h-auto lg:flex-1">
                            <TradingChart key={selectedPair} selectedPair={selectedPair} />
                          </div>
                          
                          {/* Trading Forms and Positions */}
                          <div className="flex-1 flex flex-col">
                            {/* Trading Forms */}
                            <div className="p-2 lg:p-0">
                              <FuturesTradingForms
                                getCurrentPrice={getCurrentPrice}
                                usdtBalance={balances.usdt_balance}
                                availableBalance={usdtAvailableMargin}
                                maxAllowedLeverage={currentMaxFuturesLeverage}
                                minAllowedLeverage={currentMinFuturesLeverage}
                                calculateLiquidationPrice={calculateLiquidationPrice}
                                onFuturesTrade={handleFuturesTrade}
                                selectedPair={selectedPair}
                              />
                            </div>
                            
                            {/* Positions - This div will now expand to fill available space */}
                            <div className="border-t border-slate-700 flex-1 overflow-auto">
                              <FuturesMyOrders 
                                currentBtcPrice={currentSelectedPairPrice}
                                futuresPositions={activePositions}
                                marketData={marketData}
                                onClosePosition={handleClosePosition}
                                openOrders={openOrders}
                                onCancelOrder={cancelOrder}
                                onCancelAllOrders={cancelAllOpenOrders}
                                updateBalances={updateBalances}
                                ticker={ticker}
                                balances={balances}
                                selectedPair={selectedPair}
                                tradingMode={tradingMode}
                                currentSelectedPairPrice={currentSelectedPairPrice}
                              />
                            </div>
                          </div>
                        </div>
                      
                        
                        {/* Right Column - Markets (full width on mobile, fixed width on desktop) */}
                        <div className="hidden lg:block w-full lg:w-80 border-t lg:border-l lg:border-t-0 border-slate-700 h-auto lg:h-full">
                          <Markets 
                            marketData={marketData}
                            selectedPair={selectedPair}
                            setSelectedPair={setSelectedPair}
                            currentPrice={currentSelectedPairPrice}
                            tradingMode="futures"
                          />
                        </div>
                      </div>
            } />
            <Route path={tradingPaths.cfd} element={
                      <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] bg-slate-900 overflow-y-auto">
                        {/* Left Column - Pair Details Panel (full width on mobile, fixed width on desktop) */}
                        <div className="hidden lg:block w-full lg:w-96 h-auto lg:h-full p-4 overflow-y-auto hide-scrollbar">
                          <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 h-full">
                            <PairDetailsPanel
                              currentPrice={currentSelectedPairPrice}
                              selectedPair={selectedPair}
                              tradingMode="cfd"
                              onPairSelect={setSelectedPair}
                            />
                          </div>
                        </div>

                        {/* Middle Column - Chart and Trading Forms */}
                        <div className="flex-1 flex flex-col bg-slate-900 h-full overflow-y-auto hide-scrollbar">
                          <div className="p-4 space-y-4">
                            {/* Trading Chart */}
                            <div className="h-[500px] bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden">
                              <TradingChart key={selectedPair} selectedPair={selectedPair} />
                            </div>

                            {/* Trading Forms and Positions */}
                            <div className="flex flex-col bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden">
                              {/* Trading Forms */}
                              <div className="p-4">
                                <CFDTradingForms
                                  currentPrice={currentSelectedPairPrice}
                                  usdtBalance={balances.usdt_balance}
                                  calculateLiquidationPrice={calculateLiquidationPrice}
                                  selectedPair={selectedPair}
                                  onCFDTrade={handleFuturesTrade}
                                  maxForexLeverage={currentMaxForexLeverage}
                                  minForexLeverage={currentMinForexLeverage}
                                  maxCommoditiesLeverage={currentMaxCommoditiesLeverage}
                                  minCommoditiesLeverage={currentMinCommoditiesLeverage}
                                  maxStocksLeverage={currentMaxStocksLeverage}
                                  minStocksLeverage={currentMinStocksLeverage}
                                  availableBalance={usdtAvailableMargin}
                                  userCfdTier={userCfdTier.name}
                                />
                              </div>

                              {/* Positions */}
                              <div className="border-t border-slate-700/50">
                                <FuturesMyOrders
                                  currentBtcPrice={currentSelectedPairPrice}
                                  futuresPositions={activePositions}
                                  marketData={marketData}
                                  onClosePosition={handleClosePosition}
                                  openOrders={openOrders}
                                  onCancelOrder={cancelOrder}
                                  onCancelAllOrders={cancelAllOpenOrders}
                                  updateBalances={updateBalances}
                                  ticker={ticker}
                                  balances={balances}
                                  selectedPair={selectedPair}
                                  tradingMode={tradingMode}
                                  currentSelectedPairPrice={currentSelectedPairPrice}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Right Column - Markets (full width on mobile, fixed width on desktop) */}
                        <div className="hidden lg:block w-full lg:w-80 h-auto lg:h-full p-4 overflow-y-auto hide-scrollbar">
                          <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 h-full">
                            <Markets
                              marketData={marketData}
                              selectedPair={selectedPair}
                              setSelectedPair={setSelectedPair}
                              currentPrice={currentSelectedPairPrice}
                              tradingMode="cfd"
                            />
                          </div>
                        </div>
                      </div>
            } />
            <Route path={tradingPaths.prop_firm} element={
                      <PropFirmChallengePage
                        currentPrice={currentSelectedPairPrice}
                        usdtBalance={balances.usdt_balance}
                        selectedPair={selectedPair}
                        setSelectedPair={setSelectedPair}
                        robotState={robotState}
                        updateBalances={updateBalances}
                        addTransaction={addTransaction}
                        updateRobotState={updateRobotState}
                        onClosePropPosition={async (positionId: string, livePrice?: number) => {
                          try {
                            const success = await closePropPosition(positionId, selectedChallenge?.id || '', livePrice);
                            if (success) {
                              refreshBreakdown();
                            }
                            return success;
                          } catch (error) {
                            console.error('Error closing prop position:', error);
                            return false;
                          }
                        }}
                        onCancelPropOrder={async (orderId: string) => {
                          try {
                            const success = await cancelPropOrder(orderId, selectedChallenge?.id || '');
                            return success;
                          } catch (error) {
                            console.error('Error cancelling prop order:', error);
                            return false;
                          }
                        }}
                        onCancelAllPropOrders={async () => {
                          try {
                            const success = await cancelAllPropOrders(selectedChallenge?.id || '');
                            return success;
                          } catch (error) {
                            console.error('Error cancelling all prop orders:', error);
                            return false;
                          }
                        }}
                      />
            } />
            <Route path={tradingPaths.robot} element={
                      <ArbitrageRobotPage
                        robotState={robotState}
                        updateRobotState={updateRobotState}
                        selectedPair={selectedPair}
                        setSelectedPair={setSelectedPair}
                        usdtBalance={balances.usdt_balance}
                        updateBalances={updateBalances}
                        addTransaction={addTransaction}
                        marketData={marketData}
                        usedMargin={(futuresUsedMargin || 0) + (futuresOrdersReserved || 0)}
                        availableBalance={usdtAvailableMargin}
                        refreshBreakdown={refreshBreakdown}
                        fetchBalances={fetchBalances}
                        fetchRobotState={fetchRobotState}
                      />
            } />
            <Route path={tradingPaths.events} element={
                      <EventBettingPage 
                        events={events}
                        eventOutcomes={eventOutcomes}
                        eventBets={eventBets}
                        usdtBalance={balances.usdt_balance}
                        updateBalances={updateBalances}
                        addEventBet={addEventBet}
                        addTransaction={addTransaction}
                        availableBalance={actualAvailableBalance}
                      />
            } />
            <Route path={tradingPaths.wallet} element={
                      <WalletPage 
                        usdtBalance={balances.usdt_balance}
                        btcBalance={balances.btc_balance}
                        currentBtcPrice={currentSelectedPairPrice}
                        onWalletOperation={handleWalletOperation}
                        transactions={transactions}
                        totalPortfolioValue={totalPortfolioValue}
                        userAssets={assets}
                        marketData={marketData}
                        totalBalance={totalBalance}
                        usedMargin={usedMargin}
                        unrealizedPnl={unrealizedPnl}
                        availableBalance={availableBalance}
                        reserved={reserved}
                      />
            } />
            <Route path={tradingPaths.profile} element={
                      <ProfilePage
                        user={user!}
                        onSignOut={signOut}
                        kycStatus={kycStatus}
                        updateKycStatus={updateKycStatus}
                        referralCode={referralCode}
                        referralCount={referralCount}
                        referredUsers={referredUsers}
                        portfolioSnapshots={portfolioSnapshots}
                        createPortfolioSnapshot={createPortfolioSnapshot}
                        totalPortfolioValue={totalPortfolioValue}
                        totalPositionsPnl={unrealizedPnl}
                        userStatus={userStatus}
                        onUpdatePassword={handleUpdatePassword}
                      />
            } />
            <Route path={tradingPaths.staking} element={
                      <StakingPage
                        usdtBalance={balances.usdt_balance}
                        btcBalance={balances.btc_balance}
                        currentBtcPrice={currentSelectedPairPrice}
                        onStake={handleStake}
                        userStakes={userStakes}
                        calculateCurrentEarnings={calculateCurrentEarnings}
                        cancelUserStake={cancelUserStake}
                        availableBalance={actualAvailableBalance}
                        updateBalances={updateBalances}
                        addTransaction={addTransaction}
                        fetchUserStakes={fetchUserStakes}
                        refreshBreakdown={refreshBreakdown}
                      />
            } />
            <Route path={tradingPaths.wheel} element={
                      <SpinTheWheel />
            } />
            <Route path={tradingPaths.payment_sandbox} element={
                      <PaymentSandbox />
            } />
            </Route>

            {/* Auth routes */}
            <Route path="/signin" element={
              authLoading ? <LoadingScreen /> : user ? <Navigate to={location.state?.from || "/"} replace /> : <SignInPage />
            } />
            <Route path="/signup" element={
              authLoading ? <LoadingScreen /> : user ? <Navigate to="/" replace /> : <SignUpPage />
            } />
            <Route path="/sign-up" element={
              authLoading ? <LoadingScreen /> : user ? <Navigate to="/" replace /> : <SignUpPage />
            } />
            <Route path="/forgot-password" element={
              authLoading ? <LoadingScreen /> : user ? <Navigate to="/" replace /> : <ForgotPasswordPage />
            } />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/trading-fees" element={<TradingFeesPage />} />

            {/* Test routes - only accessible when authenticated */}
            <Route path="/test/finnhub" element={
              authLoading ? <LoadingScreen /> : user ? <FinnhubWebSocketTest /> : <Navigate to="/signin" replace />
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Auth Modal */}
          {showAuthModal && (
            <AuthModal onClose={() => setShowAuthModal(false)} />
          )}

          {/* Market Loading Screen */}
          {isPreparingMarkets && (
            <MarketLoadingScreen onComplete={() => setIsPreparingMarkets(false)} />
          )}

          {/* Loading Screen */}
          {(authLoading || isLoading) && !isRecoveryLink && !isPreparingMarkets && (
            <LoadingScreen />
          )}
        </div>
    </DisplayCurrencyProvider>
  );
}

function App() {
  return (
    <Router>
      <MarketDataProvider>
        <BybitDataProvider>
          <AppContent />
        </BybitDataProvider>
      </MarketDataProvider>
    </Router>
  );
}

export default App;
